import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Edit3, ArrowRightLeft, Ban, RefreshCcw, Send,
  History, X, Loader2, Copy, ExternalLink, FileText, Trash2,
} from "lucide-react";

const FRONTEND = (typeof window !== "undefined") ? window.location.origin : "";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_FILTERS = [
  { v: "", l: "All statuses" },
  { v: "registered", l: "Registered" },
  { v: "unclaimed", l: "Unclaimed" },
  { v: "unregistered", l: "Unregistered" },
  { v: "gifted", l: "Gifted" },
  { v: "transferred", l: "Transferred" },
  { v: "voided", l: "Voided" },
];

const CHANNEL_FILTERS = [
  { v: "", l: "All channels" },
  { v: "whatnot", l: "Whatnot" },
  { v: "trade_show", l: "Trade Show" },
  { v: "cash", l: "Cash" },
  { v: "facebook_marketplace", l: "Facebook" },
  { v: "in_person", l: "In-Person" },
  { v: "gift", l: "Gift" },
  { v: "other", l: "Other" },
];

const STATUS_PILL = {
  registered: "bg-emerald-900/40 text-emerald-300 border-emerald-700/40",
  unclaimed: "bg-amber-900/40 text-amber-300 border-amber-700/40",
  unregistered: "bg-zinc-800 text-zinc-300 border-zinc-600",
  gifted: "bg-pink-900/40 text-pink-300 border-pink-700/40",
  transferred: "bg-sky-900/40 text-sky-300 border-sky-700/40",
  voided: "bg-red-900/40 text-red-300 border-red-700/40",
};

export default function AdminRegistry() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [transferTarget, setTransferTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (channel) params.set("channel", channel);
      const { data } = await http.get(`/admin/registry?${params}`);
      setRows(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load registry.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (e) => { e.preventDefault(); load(); };

  const action = async (truth_id, label, fn) => {
    try {
      await fn();
      toast.success(`${label} · ${truth_id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `${label} failed.`);
    }
  };

  const onVoid = (truth_id) => {
    if (!window.confirm(`Void truth ${truth_id}? This cannot be undone.`)) return;
    action(truth_id, "Voided", () => http.post(`/admin/registry/${truth_id}/void`));
  };
  const onDelete = (truth_id) => {
    if (!window.confirm(`PERMANENTLY DELETE ${truth_id}? This removes the row from the registry. (Use Void instead if you only want to invalidate it.)`)) return;
    action(truth_id, "Deleted", () => http.delete(`/admin/registry/${truth_id}`));
  };
  const onRegen = (truth_id) => {
    if (!window.confirm(`Regenerate claim code for ${truth_id}? The old code will stop working.`)) return;
    action(truth_id, "Claim code regenerated", () => http.post(`/admin/registry/${truth_id}/regenerate-claim`));
  };
  const onResend = (truth_id) => {
    action(truth_id, "Claim email resent", () => http.post(`/admin/registry/${truth_id}/resend-claim`));
  };

  const copy = async (txt, label = "Copied") => {
    try { await navigator.clipboard.writeText(txt); toast.success(`${label}.`); }
    catch { toast.error("Copy failed."); }
  };

  return (
    <div className="space-y-5" data-testid="admin-registry">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl text-[#F4F1EA] mb-1">Truth Registry — Admin</h2>
          <p className="font-body text-[12px] text-[#8a857d] max-w-2xl">
            Search, edit, transfer, void, and regenerate claim codes for every
            truth in the registry — web orders, off-site sales, and gifts.
          </p>
        </div>
      </div>

      <form onSubmit={onSearch} className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="block font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Search</label>
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a857d]"/>
            <input value={q} onChange={(e)=>setQ(e.target.value)}
              data-testid="reg-search"
              placeholder="Truth ID · title · email · keeper · claim code…"
              className="stc-input pl-8 w-full"/>
          </div>
        </div>
        <div>
          <label className="block font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Status</label>
          <select value={status} onChange={(e)=>setStatus(e.target.value)} data-testid="reg-status" className="stc-input">
            {STATUS_FILTERS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </div>
        <div>
          <label className="block font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Channel</label>
          <select value={channel} onChange={(e)=>setChannel(e.target.value)} data-testid="reg-channel" className="stc-input">
            {CHANNEL_FILTERS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </div>
        <button type="submit" data-testid="reg-apply" className="btn-gold">Apply</button>
        <button type="button" onClick={()=>{setQ("");setStatus("");setChannel("");setTimeout(load,0);}}
          className="btn-outline">Reset</button>
      </form>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
            <tr>
              <th className="text-left p-3">Truth ID</th>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Keeper</th>
              <th className="text-left p-3">Home</th>
              <th className="text-left p-3">Channel</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Claim</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center text-[#8a857d]">
                <Loader2 size={16} className="animate-spin inline-block mr-2"/> Loading…
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-[#8a857d] italic">No truths match these filters.</td></tr>
            ) : rows.map((t) => {
              const home = [t.city, t.state].filter(Boolean).join(", ") || "—";
              const keeper = t.steward_display_name || t.owner_display_name || t.business_name || "—";
              const st = (t.status || "").toLowerCase();
              return (
                <tr key={t.truth_id} className="border-t border-white/5 hover:bg-white/[0.02]"
                    data-testid={`reg-row-${t.truth_id}`}>
                  <td className="p-3 font-mono text-[#C9A055]">{t.truth_id}</td>
                  <td className="p-3 text-[#F2EFE8]">{t.artwork_title}</td>
                  <td className="p-3 text-[#E8E4DC]">{keeper}</td>
                  <td className="p-3 text-[#B5B0A7]">{home}</td>
                  <td className="p-3 text-[#8a857d] text-[12px]">{t.sale_channel || (t.order_number ? "web" : "—")}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 border text-[10px] font-ui tracking-[0.18em] uppercase rounded-sm ${STATUS_PILL[st] || "border-white/10 text-zinc-300"}`}>
                      {st || "—"}
                    </span>
                  </td>
                  <td className="p-3">
                    {t.claim_code ? (
                      <div className="flex items-center gap-1">
                        <code className="text-[#C9A055] font-mono text-[12px]">{t.claim_code}</code>
                        <button title="Copy claim URL" onClick={()=>copy(`${FRONTEND}/claim/${t.claim_code}`,"Claim URL copied")}
                          className="text-[#8a857d] hover:text-[#C9A055]"><Copy size={12}/></button>
                      </div>
                    ) : <span className="text-[#5a554e]">—</span>}
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1 flex-wrap justify-end">
                      <IconBtn title="Download certificate PDF" testid={`reg-cert-${t.truth_id}`}
                        onClick={()=>window.open(`${BACKEND}/api/truths/${t.truth_id}/certificate.pdf?size=letter`, "_blank", "noopener")}>
                        <FileText size={13}/></IconBtn>
                      <IconBtn title="Edit" testid={`reg-edit-${t.truth_id}`}
                        onClick={()=>setEditTarget(t)}><Edit3 size={13}/></IconBtn>
                      <IconBtn title="Transfer" testid={`reg-transfer-${t.truth_id}`}
                        onClick={()=>setTransferTarget(t)}><ArrowRightLeft size={13}/></IconBtn>
                      <IconBtn title="History" testid={`reg-history-${t.truth_id}`}
                        onClick={()=>setHistoryTarget(t)}><History size={13}/></IconBtn>
                      {t.claim_code && (
                        <>
                          <IconBtn title="Regenerate claim code" testid={`reg-regen-${t.truth_id}`}
                            onClick={()=>onRegen(t.truth_id)}><RefreshCcw size={13}/></IconBtn>
                          {t.owner_email && (
                            <IconBtn title="Resend claim email" testid={`reg-resend-${t.truth_id}`}
                              onClick={()=>onResend(t.truth_id)}><Send size={13}/></IconBtn>
                          )}
                        </>
                      )}
                      <IconBtn title="Void" testid={`reg-void-${t.truth_id}`}
                        onClick={()=>onVoid(t.truth_id)} danger><Ban size={13}/></IconBtn>
                      <IconBtn title="Delete (permanent)" testid={`reg-delete-${t.truth_id}`}
                        onClick={()=>onDelete(t.truth_id)} danger><Trash2 size={13}/></IconBtn>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editTarget && (
        <EditDialog truth={editTarget} onClose={()=>setEditTarget(null)} onSaved={()=>{setEditTarget(null);load();}}/>
      )}
      {transferTarget && (
        <TransferDialog truth={transferTarget} onClose={()=>setTransferTarget(null)} onSaved={()=>{setTransferTarget(null);load();}}/>
      )}
      {historyTarget && (
        <HistoryDrawer truth={historyTarget} onClose={()=>setHistoryTarget(null)}/>
      )}
    </div>
  );
}

function IconBtn({ children, onClick, title, testid, danger }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className={`p-1.5 border rounded-sm transition-colors ${danger ? "border-red-700/40 text-red-300 hover:bg-red-900/30" : "border-white/10 text-[#8a857d] hover:border-[#C9A055] hover:text-[#C9A055]"}`}>
      {children}
    </button>
  );
}

function EditDialog({ truth, onClose, onSaved }) {
  const [form, setForm] = useState({
    owner_display_name: truth.owner_display_name || "",
    steward_display_name: truth.steward_display_name || "",
    owner_email: truth.owner_email || "",
    city: truth.city || "",
    state: truth.state || "",
    country: truth.country || "",
    privacy: truth.privacy || "public",
    display_preference: truth.display_preference || "name_and_location",
    notes: truth.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k,v)=>setForm({...form,[k]:v});

  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/admin/registry/${truth.truth_id}`, form);
      toast.success("Saved.");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <Modal title={`Edit · ${truth.truth_id}`} onClose={onClose} testid="reg-edit-modal">
      <div className="space-y-3">
        <Row label="Truth Keeper name">
          <input value={form.steward_display_name} onChange={(e)=>set("steward_display_name",e.target.value)}
            data-testid="reg-edit-keeper" className="stc-input w-full"/>
        </Row>
        <Row label="Owner full name">
          <input value={form.owner_display_name} onChange={(e)=>set("owner_display_name",e.target.value)}
            className="stc-input w-full"/>
        </Row>
        <Row label="Owner email">
          <input value={form.owner_email} onChange={(e)=>set("owner_email",e.target.value)}
            data-testid="reg-edit-email" className="stc-input w-full"/>
        </Row>
        <div className="grid grid-cols-3 gap-2">
          <Row label="City"><input value={form.city} onChange={(e)=>set("city",e.target.value)} data-testid="reg-edit-city" className="stc-input w-full"/></Row>
          <Row label="State"><input value={form.state} onChange={(e)=>set("state",e.target.value)} data-testid="reg-edit-state" className="stc-input w-full"/></Row>
          <Row label="Country"><input value={form.country} onChange={(e)=>set("country",e.target.value)} data-testid="reg-edit-country" className="stc-input w-full"/></Row>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Row label="Privacy">
            <select value={form.privacy} onChange={(e)=>set("privacy",e.target.value)} data-testid="reg-edit-privacy" className="stc-input w-full">
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="city_only">City only</option>
              <option value="business">Business</option>
            </select>
          </Row>
          <Row label="Display preference">
            <select value={form.display_preference} onChange={(e)=>set("display_preference",e.target.value)} data-testid="reg-edit-display-pref" className="stc-input w-full">
              <option value="name_and_location">Name & location</option>
              <option value="first_name">First name only</option>
              <option value="anonymous">Anonymous Truth Keeper</option>
              <option value="hide_location">Show name, hide location</option>
              <option value="private">Private registry only</option>
            </select>
          </Row>
        </div>
        <Row label="Internal notes">
          <textarea value={form.notes} onChange={(e)=>set("notes",e.target.value)} rows={2} className="stc-input w-full"/>
        </Row>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="btn-outline">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="reg-edit-save" className="btn-gold">
          {busy ? <Loader2 size={13} className="animate-spin"/> : "Save"}
        </button>
      </div>
    </Modal>
  );
}

function TransferDialog({ truth, onClose, onSaved }) {
  const [form, setForm] = useState({
    new_owner_name: "", new_owner_email: "",
    city: "", state: "", country: "", reason: "",
  });
  const [busy, setBusy] = useState(false);
  const set=(k,v)=>setForm({...form,[k]:v});

  const submit = async () => {
    if (!form.new_owner_name.trim()) { toast.error("New owner name required."); return; }
    setBusy(true);
    try {
      await http.post(`/admin/registry/${truth.truth_id}/transfer`, {
        ...form,
        new_owner_email: form.new_owner_email || null,
        city: form.city || null, state: form.state || null, country: form.country || null,
        reason: form.reason || null,
      });
      toast.success("Transferred.");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Transfer failed.");
    } finally { setBusy(false); }
  };

  return (
    <Modal title={`Transfer · ${truth.truth_id}`} onClose={onClose} testid="reg-transfer-modal">
      <p className="font-body text-[12px] text-[#8a857d] mb-3">
        Re-assign this truth to a new keeper. The previous owner is preserved in history.
      </p>
      <div className="space-y-3">
        <Row label="New owner name *">
          <input value={form.new_owner_name} onChange={(e)=>set("new_owner_name",e.target.value)}
            data-testid="reg-transfer-name" className="stc-input w-full" required/>
        </Row>
        <Row label="New owner email">
          <input value={form.new_owner_email} onChange={(e)=>set("new_owner_email",e.target.value)}
            data-testid="reg-transfer-email" className="stc-input w-full"/>
        </Row>
        <div className="grid grid-cols-3 gap-2">
          <Row label="City"><input value={form.city} onChange={(e)=>set("city",e.target.value)} className="stc-input w-full"/></Row>
          <Row label="State"><input value={form.state} onChange={(e)=>set("state",e.target.value)} className="stc-input w-full"/></Row>
          <Row label="Country"><input value={form.country} onChange={(e)=>set("country",e.target.value)} className="stc-input w-full"/></Row>
        </div>
        <Row label="Reason / note">
          <input value={form.reason} onChange={(e)=>set("reason",e.target.value)}
            className="stc-input w-full" placeholder="Gifted, resold, returned…"/>
        </Row>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="btn-outline">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="reg-transfer-save" className="btn-gold">
          {busy ? <Loader2 size={13} className="animate-spin"/> : "Transfer"}
        </button>
      </div>
    </Modal>
  );
}

function HistoryDrawer({ truth, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    http.get(`/admin/registry/${truth.truth_id}/history`)
      .then(({data})=>setHistory(data.history || []))
      .catch(()=>toast.error("Failed to load history."))
      .finally(()=>setLoading(false));
  }, [truth.truth_id]);

  return (
    <Modal title={`Ownership History · ${truth.truth_id}`} onClose={onClose} testid="reg-history-modal" wide>
      {loading ? (
        <div className="py-8 text-center text-[#8a857d]"><Loader2 size={16} className="animate-spin inline-block"/></div>
      ) : history.length === 0 ? (
        <div className="py-8 text-center text-[#8a857d] italic">No history recorded for this truth.</div>
      ) : (
        <ol className="relative border-l border-[#C9A055]/30 pl-6 space-y-5">
          {history.map((h, i) => (
            <li key={i} className="relative" data-testid={`reg-history-event-${i}`}>
              <span className="absolute -left-[31px] top-1 w-3 h-3 rounded-full bg-[#C9A055] ring-4 ring-[#0a0a0a]"/>
              <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055]">{h.event || "event"}</div>
              <div className="font-body text-[11px] text-[#8a857d]">{h.at ? new Date(h.at).toLocaleString() : ""}</div>
              <div className="mt-1 text-[13px] text-[#E8E4DC] space-y-0.5">
                {h.name && <div><b>{h.name}</b>{h.email ? ` · ${h.email}` : ""}</div>}
                {(h.city || h.state || h.country) && <div className="text-[#B5B0A7]">📍 {[h.city,h.state,h.country].filter(Boolean).join(", ")}</div>}
                {h.previous && (
                  <div className="text-[12px] text-[#8a857d]">previous: {h.previous.owner_display_name || h.previous.steward_display_name || "—"}</div>
                )}
                {h.to_name && <div>→ {h.to_name}{h.to_email ? ` (${h.to_email})` : ""}</div>}
                {h.channel && <div className="text-[12px] text-[#8a857d]">channel: {h.channel}{h.price ? ` · $${h.price}` : ""}</div>}
                {h.display_preference && <div className="text-[12px] text-[#8a857d]">display: {h.display_preference}</div>}
                {h.old_code && <div className="text-[12px] text-[#8a857d]">{h.old_code} → <span className="text-[#C9A055]">{h.new_code}</span></div>}
                {h.fields && <div className="text-[12px] text-[#8a857d]">changed: {(h.fields||[]).join(", ")}</div>}
                {h.reason && <div className="text-[12px] text-[#B5B0A7] italic">&ldquo;{h.reason}&rdquo;</div>}
              </div>
            </li>
          ))}
        </ol>
      )}
      <div className="mt-5 flex justify-between items-center pt-4 border-t border-white/10">
        <a href={`/truths/${truth.truth_id}`} target="_blank" rel="noreferrer"
          className="text-[#C9A055] text-[12px] flex items-center gap-1 hover:underline">
          <ExternalLink size={12}/> Open public truth page
        </a>
        <button onClick={onClose} className="btn-outline">Close</button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-[200] bg-black/80 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose} data-testid={testid}>
      <div onClick={(e)=>e.stopPropagation()}
        className={`bg-[#0a0a0a] border border-white/10 my-12 w-full ${wide ? "max-w-2xl" : "max-w-lg"} p-5`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-xl text-[#F4F1EA]">{title}</h3>
          <button onClick={onClose} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={16}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">{label}</div>
      {children}
    </label>
  );
}
